import type { PlanId } from '@/lib/branchTypes'
import { DEFAULT_ANIMATION_TUNING, resolveJsEasing } from '@/app/playbook/animation/animationTuningDefaults'
import speedupRawGraphData from './extrapolatedGraphData.json'
import type {
  ExtrapolatedGraphScaleSettings,
  GraphDataFile,
  PlanKey,
} from './types'

export const defaultGraphData = speedupRawGraphData as unknown as GraphDataFile

export const PLAN_KEYS: Record<PlanId, PlanKey> = {
  S: 'planS', A: 'planA', B: 'planB', C: 'planC', D: 'planD',
}

export const DEFAULT_SCALE_SETTINGS: ExtrapolatedGraphScaleSettings = {
  padding: { top: 7, right: 10, bottom: 18, left: 34 },
  yAxis: {
    mode: 'fixed-until-threshold',
    fixedMax:90,
    switchThreshold: 1500,
    tickCount: 4,
  },
  timeline: {
    futureWindowYears: 8,
  },
  galaxy: {
    referenceYear: 2023,
    maxYear: 3200,
    tickYears: [2040, 2400, 3200],
  },
  animation: {
    smoothingFactor: DEFAULT_ANIMATION_TUNING.viewportSmoothingFactor,
    snapThresholdMs: 3600000,
  },
}

// ── Epilogue HUD transition ──
// Entering the epilogue: the rest of the HUD fades away fast
// (EPILOGUE_HUD_FADE_OUT_MS); the graph — shrinking and sliding down to rest
// above the space-parcel panel — moves quickly together with the parcel and
// the year pill (EPILOGUE_GRAPH_MOVE_DURATION_MS); only the Earth drifts to
// screen center on its own slower clock (EPILOGUE_MOVE_DURATION_MS).
// Scrolling back up plays everything in reverse (all of it is CSS
// transitions / retargeted tweens on stable elements, so reversal mid-flight
// is smooth too). After the graph's move settles, the graph tracks parcel
// height changes with NO transition, staying in lockstep with the parcel's
// own animated rows (see useHudEpilogueMode).
export const EPILOGUE_MOVE_DURATION_MS = DEFAULT_ANIMATION_TUNING.epilogueGlobeMoveMs
// easeInOutCubic — pronounced slow start/end.
export const EPILOGUE_MOVE_EASING = DEFAULT_ANIMATION_TUNING.epilogueMoveEasing
// JS twin of EPILOGUE_MOVE_EASING for rAF-driven epilogue tweens (the graph
// height, the year pill's drop) — kept as a module constant because
// ExtrapolatedGraphRenderer consumes it directly. Derived from the same tuning
// spec so it stays in lockstep with the CSS transitions.
export const epilogueMoveEase = resolveJsEasing(DEFAULT_ANIMATION_TUNING.epilogueMoveEasing)
export const EPILOGUE_GRAPH_MOVE_DURATION_MS = DEFAULT_ANIMATION_TUNING.epilogueGraphMoveMs
export const EPILOGUE_HUD_FADE_OUT_MS = DEFAULT_ANIMATION_TUNING.epilogueHudFadeMs

// THE shared HUD transition window: when the story date jumps (scroll passes
// a marker, plan hover/switch), every side-panel element — the date tween,
// graph lines, viewport, dither bands, power treemap, metric tickers —
// starts at the jump and completes within this window, so the whole panel
// reads as one synchronized change.
export const HUD_SYNC_TRANSITION_MS = DEFAULT_ANIMATION_TUNING.hudSyncTransitionMs

export const LOSS_OF_CONTROL_TRANSITION_DURATION_MS = HUD_SYNC_TRANSITION_MS

export const CAPABILITIES_GRAPH_TRANSITION_DURATION_MS = HUD_SYNC_TRANSITION_MS
export const GRAPH_VIEWPORT_TRANSITION_DURATION_MS = HUD_SYNC_TRANSITION_MS

export const GALAXY_BACKGROUND_COLOR = '#C0BDAD'
export const GRAPH_ACCENT_COLOR = 'var(--race-background)'
export const GRAPH_BACKGROUND_ACCENT_COLOR = 'var(--race-background)'
export const GRAPH_FUTURE_COLOR = '#555'
/**
 * Main AI capabilities curve (past solid + future dashed) and matching
 * markers; not plan/accent dependent.
 */
export const CAPABILITIES_LINE_COLOR = '#000000'
/**
 * Plan B regional comparison: China speedup spine (drawn from `planBCh` when
 * the toggle is on).
 */
export const PLAN_B_CHINA_CAPABILITY_LINE_COLOR = '#a61d1d'
/**
 * Set to true to draw `planBCh` alongside the US (`planBUs`) curve while
 * Plan B is active.
 */
export const SHOW_PLAN_B_CHINA_CAPABILITY_LINE = false

/**
 * Duration for Plan B China capability curve "growing" from the baseline
 * (linear global clock; per-point easing inside).
 */
export const CHINA_LINE_RISE_DURATION_MS = 1400
export const PROJECTED_LOSS_OF_CONTROL_COLOR = '#d22'
export const PROJECTED_LOSS_OF_CONTROL_LOW_COLOR = '#f28c28'
export const PROJECTED_LOSS_OF_CONTROL_2028_OPACITY = 0.3
export const PROJECTED_LOSS_OF_CONTROL_2028_SATURATION = 0
export const GRAPH_LINE_STROKE_WIDTH = 4
export const GRAPH_LABEL_FADE_DURATION_MS = 750

export const LOSS_OF_CONTROL_BOUNDARY_LINE_COLOR = 'var(--race-background)'
export const LOSS_OF_CONTROL_BOUNDARY_LINE_STROKE_WIDTH = 0
export const LOSS_OF_CONTROL_BOUNDARY_LINE_OPACITY = 1

export const CAPABILITIES_FUTURE_LINE_STROKE_WIDTH = 2.5
export const CAPABILITIES_FUTURE_LINE_DASHARRAY = '4,6'
// Gap (px) between dashes on the future capability line. The dash length
// itself is a live ⌘+G setting (`capabilityFutureLineDashLength`); this gap
// stays fixed so adjusting dash length doesn't also re-space the gaps.
export const CAPABILITIES_FUTURE_LINE_DASH_GAP = 6
export const CAPABILITIES_FUTURE_DOT_RADIUS = 1
export const CAPABILITIES_PAST_DOT_RADIUS = 2.5
export const CAPABILITIES_CURRENT_DOT_RADIUS = 3

export const RISK_BACKGROUND_LABELS = [
  // Risk labels are intentionally opt-in data; the graph can show the red
  // background without labeling the gradient.
]
export const RISK_BACKGROUND_LABEL_GUIDE_STROKE = {
  opacity: 0,
  color: '#fff',
  thickness: 2,
  dasharray: '4 3',
  borderColor: 'var(--race-background)',
  borderThickness: 4,
}

export const EC_TO_UPLIFT_REFERENCE_POINTS = [
  { effectiveCompute: 0, uplift: 3 },
  { effectiveCompute: 0.94, uplift: 10.03 },
  { effectiveCompute: 2.2, uplift: 31.04 },
  { effectiveCompute: 3.28, uplift: 76.93 },
  { effectiveCompute: 4.19, uplift: 133.94 },
  { effectiveCompute: 5, uplift: 200.3 },
]
